import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { isEqual } from 'lodash';

import { CipoDialogHeaderComponent } from '../cipo-dialog-header/cipo-dialog-header.component';
import { DirtyDialogBase } from '../dirty-dialog.base';

export interface PermissionsDialogPermission {
  id: number;
  name: string;
  description?: string;
  isUsed: boolean;
  isDefault: boolean;
  filterId?: number;
  filters: { key: number; value: string; description?: string }[];
}

export interface PermissionsDialogData<T extends PermissionsDialogPermission = PermissionsDialogPermission> {
  title: string;
  subtitle?: string;
  permissions: T[];
  canUpdate: boolean;
}

@Component({
  selector: 'cipo-permissions-dialog',
  templateUrl: './permissions-dialog.component.html',
  imports: [
    CipoDialogHeaderComponent,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatDialogModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionsDialogComponent<
  T extends PermissionsDialogPermission = PermissionsDialogPermission,
> extends DirtyDialogBase {
  private readonly data = inject<PermissionsDialogData<T>>(MAT_DIALOG_DATA);
  protected override readonly dialogRef = inject(MatDialogRef<PermissionsDialogComponent<T>, T[]>);

  readonly title = this.data.title;
  readonly subtitle = this.data.subtitle;
  readonly canUpdate = this.data.canUpdate;
  readonly permissions = signal(this.clonePermissions(this.data.permissions));
  private readonly savedSelection = this.selection(this.data.permissions);
  protected readonly isDirty = computed(() => !isEqual(this.selection(this.permissions()), this.savedSelection));

  readonly allSelected = computed(() => {
    const editablePermissions = this.permissions().filter(permission => !permission.isDefault);
    return editablePermissions.length > 0 && editablePermissions.every(permission => permission.isUsed);
  });

  togglePermission(permission: T): void {
    if (!this.canUpdate || permission.isDefault) return;

    permission.isUsed = !permission.isUsed;
    if (permission.isUsed && !permission.filterId && permission.filters?.length) {
      permission.filterId = permission.filters[0].key;
    }
    this.notifyChanged();
  }

  setFilterId(permission: T, filterId: number): void {
    if (!this.canUpdate || !permission.isUsed) return;

    permission.filterId = filterId;
    this.notifyChanged();
  }

  selectAll(isSelected: boolean): void {
    if (!this.canUpdate) return;

    this.permissions().forEach(permission => {
      if (permission.isDefault) return;

      permission.isUsed = isSelected;
      if (permission.filters?.length && (!permission.filterId || !isSelected)) {
        permission.filterId = permission.filters[0].key;
      }
    });
    this.notifyChanged();
  }

  apply(): void {
    this.dialogRef.close(this.clonePermissions(this.permissions()));
  }

  private notifyChanged(): void {
    this.permissions.update(permissions => [...permissions]);
  }

  private selection(permissions: T[]) {
    return (permissions || []).map(({ isUsed, filterId }) => (isUsed ? [true, filterId ?? null] : [false]));
  }

  private clonePermissions(permissions: T[]): T[] {
    return (permissions || []).map(permission => ({
      ...permission,
      filters: (permission.filters || []).map(filter => ({ ...filter })),
    }));
  }
}
